/*
  Emily Harrison London: cart integration (chrome builder).

  One cart client for the whole theme, built on Shopify's Ajax Cart API with the
  locale aware routes printed in layout/theme.liquid (window.EHL.routes):
  - add:     POST {cart_add_url}.js     (FormData, keeps line item properties)
  - change:  POST {cart_change_url}.js  ({ id: line key, quantity })
  - note:    POST {cart_update_url}.js  ({ note })
  - refresh: GET  {current path}?sections=...
  Every mutation asks Shopify to re-render the cart sections in the same request
  (Section Rendering API: the "cart-drawer" section plus the cart page section when it is
  on the page), so prices, compare at prices, discount allocations and totals always come
  from the authoritative cart. Requests run one at a time through a queue, so rapid
  changes are applied in order and the last render always reflects the final cart.
  Shopify issues new line item keys whenever a line's discount allocation changes (for
  example when the multi item tier changes), so a line is identified by a stable reference
  (variant id, properties and selling plan, rendered as data attributes) and its current key
  is resolved when the queued request actually runs, from the latest cart JSON.

  Public API (used by the product page, see content/fragments/chrome.theme-liquid.md):
    window.EHL.cart.add(formData, { trigger }) -> Promise (rejects with a displayable Error)
    window.EHL.cart.change(lineKeyOrElement, quantity) -> Promise
    window.EHL.cart.refresh()                  -> Promise
    window.EHL.cart.open(trigger) / close()
  Events dispatched on document: 'ehl:cart:updated' { source, cart? }, 'ehl:cart:error' { message }.
  Forms with [data-product-form] (or action ending in /cart/add) that no other script
  handled are submitted through the same client.
*/
(() => {
  if (window.EHL && window.EHL.cart && window.EHL.cart.__ehl) return;

  const EHL = (window.EHL = window.EHL || {});
  const strings = EHL.strings || {};
  const cartSettings = EHL.cartSettings || { type: 'drawer' };

  const ensureSlash = (value) => (value.endsWith('/') ? value : `${value}/`);
  const root = ensureSlash(
    (EHL.routes && EHL.routes.root) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
  );
  const routes = {
    cart: (EHL.routes && EHL.routes.cart) || `${root}cart`,
    cartAdd: (EHL.routes && EHL.routes.cartAdd) || `${root}cart/add`,
    cartChange: (EHL.routes && EHL.routes.cartChange) || `${root}cart/change`,
    cartUpdate: (EHL.routes && EHL.routes.cartUpdate) || `${root}cart/update`,
  };

  const DRAWER_SECTION_ID = 'cart-drawer';
  const GENERIC_ERROR = strings.cartError || 'Something went wrong. Please try again.';
  const ADD_ERROR = strings.cartAddError || GENERIC_ERROR;

  // Shopify's own wording is kept only for availability and inventory messages (status 422,
  // already in the shopper's language). Technical API messages are replaced by theme strings.
  const TECHNICAL_MESSAGE = /cannot find variant|no valid id|line parameter|parameter missing|invalid|bad.request/i;
  const shopperMessage = (status, data, fallback) => {
    const raw = data && (data.description || data.message || (typeof data.errors === 'string' ? data.errors : ''));
    if (status === 422 && typeof raw === 'string' && raw.trim() && !TECHNICAL_MESSAGE.test(raw)) return raw;
    return fallback;
  };

  class CartRequestError extends Error {
    constructor(message, status, data) {
      super(message || GENERIC_ERROR);
      this.name = 'CartRequestError';
      this.status = status;
      this.data = data;
    }
  }

  const dispatch = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ------------------------------------------------------------------ */
  /* Cart client                                                        */
  /* ------------------------------------------------------------------ */
  class CartClient {
    constructor() {
      this.queue = Promise.resolve();
      this.pending = 0;
      // Cart JSON returned by the last change (null when unknown, for example after an add).
      this.latestCart = null;
    }

    get busy() {
      return this.pending > 0;
    }

    sectionIds() {
      const ids = [DRAWER_SECTION_ID];
      document.querySelectorAll('[data-cart-section-id]').forEach((node) => {
        const id = node.dataset.cartSectionId;
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids.slice(0, 5);
    }

    enqueue(task) {
      this.pending += 1;
      document.documentElement.classList.add('cart-is-updating');
      const run = this.queue.then(task, task);
      this.queue = run
        .catch(() => {})
        .finally(() => {
          this.pending -= 1;
          if (!this.pending) {
            document.documentElement.classList.remove('cart-is-updating');
            dispatch('ehl:cart:idle', {});
          }
        });
      return run;
    }

    idle() {
      return this.queue;
    }

    async send(url, options, fallback = GENERIC_ERROR) {
      let response;
      try {
        response = await fetch(url, options);
      } catch (error) {
        throw new CartRequestError(fallback, 0, null);
      }
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }
      if (!response.ok || !data || (typeof data.status === 'number' && data.status >= 400) || data.errors) {
        throw new CartRequestError(shopperMessage(response.status, data, fallback), response.status, data);
      }
      return data;
    }

    async fetchCart() {
      const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new CartRequestError(GENERIC_ERROR, response.status, null);
      const cart = await response.json();
      this.latestCart = cart;
      return cart;
    }

    // Current key of a line: the clicked key if the latest cart still has it, otherwise the
    // line with the same variant, properties and selling plan in the latest (or a fresh) cart.
    async resolveKey(ref, forceFresh) {
      const cart = forceFresh ? await this.fetchCart() : this.latestCart;
      // Nothing known yet: the rendered key is the best guess (a stale key gets one retry).
      if (!cart || !Array.isArray(cart.items)) return ref.key;
      if (cart.items.some((item) => item.key === ref.key)) return ref.key;
      const matched = matchLine(cart, ref);
      if (matched) return matched;
      return forceFresh ? null : this.resolveKey(ref, true);
    }

    add(formData) {
      return this.enqueue(async () => {
        const ids = this.sectionIds();
        formData.set('sections', ids.join(','));
        formData.set('sections_url', window.location.pathname);
        const data = await this.send(
          `${routes.cartAdd}.js`,
          {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          },
          ADD_ERROR
        );
        // An add returns the added item, not the cart: the next change resolves keys afresh.
        this.latestCart = null;
        if (data.sections) {
          renderSections(data.sections);
        } else {
          await this.fetchSections();
        }
        dispatch('ehl:cart:updated', { source: 'add', item: data });
        return data;
      });
    }

    change(ref, quantity) {
      return this.enqueue(async () => {
        const ids = this.sectionIds();
        const post = (key) =>
          this.send(`${routes.cartChange}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: key, quantity, sections: ids, sections_url: window.location.pathname }),
          });
        let key = await this.resolveKey(ref, false);
        let data;
        try {
          data = await post(key);
        } catch (error) {
          // A 400 means the key went stale in the meantime (re-keyed by a discount change, or
          // changed in another tab): look the line up in a fresh cart and try once more.
          if (error.status !== 400) throw error;
          const fresh = await this.resolveKey(ref, true);
          if (!fresh || fresh === key) throw error;
          key = fresh;
          data = await post(key);
        }
        const { sections, ...cart } = data;
        this.latestCart = cart;
        if (sections) {
          renderSections(sections);
        } else {
          await this.fetchSections();
        }
        dispatch('ehl:cart:updated', { source: 'change', cart });
        return { cart, key };
      });
    }

    updateNote(note) {
      return this.enqueue(async () => {
        const data = await this.send(`${routes.cartUpdate}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ note }),
        });
        dispatch('ehl:cart:updated', { source: 'note', cart: data });
        return data;
      });
    }

    // Section Rendering API. No Accept header: Shopify varies on Accept and answers
    // "application/json" on product, collection, cart and page URLs with the resource JSON
    // instead of the sections payload.
    async fetchSections() {
      const ids = this.sectionIds();
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url.toString(), { cache: 'no-store' });
      if (!response.ok) throw new CartRequestError(GENERIC_ERROR, response.status, null);
      let sections = null;
      try {
        sections = await response.json();
      } catch (error) {
        sections = null;
      }
      if (!sections || !ids.some((id) => typeof sections[id] === 'string')) {
        throw new CartRequestError(GENERIC_ERROR, response.status, null);
      }
      renderSections(sections);
      return sections;
    }

    refresh() {
      return this.enqueue(async () => {
        this.latestCart = null;
        await this.fetchSections();
        dispatch('ehl:cart:updated', { source: 'refresh' });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Line references                                                     */
  /* ------------------------------------------------------------------ */
  const normaliseProperties = (value) => {
    let object = value;
    if (typeof object === 'string') {
      try {
        object = JSON.parse(object || '{}');
      } catch (error) {
        object = {};
      }
    }
    if (!object || typeof object !== 'object') object = {};
    return JSON.stringify(
      Object.keys(object)
        .sort()
        .map((name) => [name, object[name] == null ? '' : String(object[name])])
    );
  };

  const sellingPlanOf = (item) =>
    item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? String(item.selling_plan_allocation.selling_plan.id)
      : '';

  // ref: { id, key, variantId, properties, sellingPlan, index } read from a rendered line.
  const matchLine = (cart, ref) => {
    if (!cart || !Array.isArray(cart.items) || !ref || !ref.variantId) return null;
    const props = normaliseProperties(ref.properties);
    const candidates = cart.items
      .map((item, index) => ({ item, index }))
      .filter(
        ({ item }) =>
          String(item.variant_id) === String(ref.variantId) &&
          normaliseProperties(item.properties) === props &&
          sellingPlanOf(item) === (ref.sellingPlan || '')
      );
    if (!candidates.length) return null;
    const samePosition = candidates.find(({ index }) => index + 1 === ref.index);
    return (samePosition || candidates[0]).item.key;
  };

  const lineRef = (item) => ({
    id: item.dataset.cartLineRef || item.dataset.cartItemKey,
    key: item.dataset.cartItemKey,
    variantId: item.dataset.cartVariantId || '',
    properties: item.dataset.cartItemProperties || '{}',
    sellingPlan: item.dataset.cartSellingPlan || '',
    index: Number(item.dataset.cartLineIndex || 0),
  });

  const client = new CartClient();

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const pendingRefs = new Set();

  const rememberFocus = (container) => {
    const active = document.activeElement;
    if (!active || !container || !container.contains(active)) return null;
    const holder = active.closest('[data-focus-id]');
    const item = active.closest('[data-cart-item-key]');
    const items = [...container.querySelectorAll('[data-cart-item-key]')];
    return {
      focusId: holder ? holder.dataset.focusId : null,
      index: item ? items.indexOf(item) : -1,
    };
  };

  const restoreFocus = (container, memory, fallback) => {
    if (!memory) return;
    let target = null;
    if (memory.focusId) {
      target = container.querySelector(`[data-focus-id="${CSS.escape(memory.focusId)}"]`);
    }
    if (!target) {
      const items = [...container.querySelectorAll('[data-cart-item-key]')];
      const next = items[Math.min(Math.max(memory.index, 0), items.length - 1)];
      target = next ? next.querySelector('.cart-drawer__item-title, .cart-page__item-title, [data-focus-id]') : null;
    }
    if (!target) target = fallback;
    if (target) target.focus({ preventScroll: true });
  };

  const applyPending = (container) => {
    pendingRefs.forEach((ref) => setLinePending(container, ref, true));
  };

  const updateBadges = (doc) => {
    const source = doc.querySelector('[data-cart-drawer]');
    if (!source) return;
    const count = Number(source.dataset.cartItemCount || 0);
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((node) => {
      node.textContent = source.dataset.cartBadgeLabel || '';
    });
    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      drawer.dataset.cartItemCount = String(count);
      drawer.dataset.cartLive = source.dataset.cartLive || '';
      const countNode = drawer.querySelector('[data-cart-drawer-count]');
      if (countNode) countNode.textContent = String(count);
      const live = drawer.querySelector('[data-cart-live-region]');
      if (live) live.textContent = source.dataset.cartLive || '';
    }
  };

  const renderSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== 'string') return;
      const doc = parseHTML(html);
      if (id === DRAWER_SECTION_ID) {
        const drawer = document.querySelector('[data-cart-drawer]');
        const next = doc.querySelector('[data-cart-drawer-contents]');
        const current = drawer && drawer.querySelector('[data-cart-drawer-contents]');
        if (current && next) {
          const memory = rememberFocus(current);
          current.innerHTML = next.innerHTML;
          applyPending(current);
          restoreFocus(current, memory, drawer.querySelector('#CartDrawerTitle'));
        }
        updateBadges(doc);
        return;
      }
      const host = document.querySelector(`[data-cart-section-id="${CSS.escape(id)}"]`);
      const next = doc.querySelector('[data-cart-page-contents]');
      const current = host && host.querySelector('[data-cart-page-contents]');
      if (current && next) {
        const memory = rememberFocus(current);
        current.innerHTML = next.innerHTML;
        applyPending(current);
        restoreFocus(current, memory, current.querySelector('[data-cart-page-title]'));
      }
    });
  };

  // Pending lines keep their controls focusable (aria-disabled instead of disabled), so keyboard
  // focus stays on the button that was pressed; changeLine ignores lines that are pending.
  const lineSelector = (ref) =>
    `[data-cart-line-ref="${CSS.escape(ref)}"], [data-cart-item-key="${CSS.escape(ref)}"]`;

  const setLinePending = (container, ref, isPending) => {
    if (!container) return;
    container.querySelectorAll(lineSelector(ref)).forEach((item) => {
      item.classList.toggle('is-pending', isPending);
      item.setAttribute('aria-busy', isPending ? 'true' : 'false');
      item.querySelectorAll('[data-cart-quantity-change], [data-cart-remove], [data-cart-quantity-input]').forEach((control) => {
        if (isPending) control.setAttribute('aria-disabled', 'true');
        else control.removeAttribute('aria-disabled');
        if (control.matches('input')) control.readOnly = isPending;
      });
    });
  };

  const setAllLinesPending = (ref, isPending) => {
    document.querySelectorAll('[data-cart-drawer-contents], [data-cart-page-contents]').forEach((container) => {
      setLinePending(container, ref, isPending);
    });
  };

  const showContainerError = (container, message) => {
    if (!container) return;
    const node = container.querySelector('[data-cart-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  };

  const showLineError = (ref, message) => {
    let shown = false;
    document.querySelectorAll(lineSelector(ref)).forEach((item) => {
      item.querySelectorAll('[data-cart-item-error]').forEach((node) => {
        node.textContent = message;
        node.hidden = false;
        shown = true;
      });
    });
    return shown;
  };

  const formatLimitMessage = (title, quantity) =>
    (strings.cartQuantityLimited || 'Only [quantity] of [title] can be added.')
      .replace('[quantity]', String(quantity))
      .replace('[title]', title || '');

  /* ------------------------------------------------------------------ */
  /* Line changes (drawer and cart page share these handlers)            */
  /* ------------------------------------------------------------------ */
  // target: a rendered line element, or a line key (public API).
  const changeLine = async (target, quantity) => {
    const ref =
      target instanceof Element ? lineRef(target) : target ? { id: String(target), key: String(target) } : null;
    if (!ref || !ref.key || pendingRefs.has(ref.id)) return;
    const requested = Math.max(0, Math.floor(Number(quantity) || 0));
    pendingRefs.add(ref.id);
    setAllLinesPending(ref.id, true);
    try {
      const { cart, key } = await client.change(ref, requested);
      pendingRefs.delete(ref.id);
      setAllLinesPending(ref.id, false);
      if (requested > 0 && cart && Array.isArray(cart.items)) {
        const line = cart.items.find((item) => item.key === key);
        if (line && line.quantity < requested) {
          showLineError(ref.id, formatLimitMessage(line.product_title, line.quantity));
        }
      }
    } catch (error) {
      pendingRefs.delete(ref.id);
      setAllLinesPending(ref.id, false);
      // Reconcile with the authoritative cart, then show the reason.
      try {
        await client.refresh();
      } catch (refreshError) {
        /* keep the current markup */
      }
      const message = error instanceof CartRequestError ? error.message : GENERIC_ERROR;
      if (!showLineError(ref.id, message)) {
        document.querySelectorAll('[data-cart-drawer-contents], [data-cart-page-contents]').forEach((container) => {
          showContainerError(container, message);
        });
      }
      dispatch('ehl:cart:error', { message });
    }
  };

  const bindLineControls = (container) => {
    container.addEventListener('click', (event) => {
      const stepper = event.target.closest('[data-cart-quantity-change]');
      if (stepper) {
        const item = stepper.closest('[data-cart-item-key]');
        if (!item || stepper.getAttribute('aria-disabled') === 'true') return;
        const input = item.querySelector('[data-cart-quantity-input]');
        const current = Number(input ? input.value : item.dataset.cartItemQuantity) || 0;
        const delta = Number(stepper.dataset.cartQuantityDelta || 0);
        changeLine(item, Math.max(0, current + delta));
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        const item = remove.closest('[data-cart-item-key]');
        if (!item || remove.getAttribute('aria-disabled') === 'true') return;
        changeLine(item, 0);
      }
    });

    container.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (!input) return;
      const item = input.closest('[data-cart-item-key]');
      if (!item || input.readOnly) return;
      // Only a whole number from 0 up is applied (0 removes the line, like Remove). An emptied
      // field, a negative or a decimal number restores the current quantity instead.
      const raw = String(input.value).trim();
      if (!/^\d+$/.test(raw)) {
        input.value = item.dataset.cartItemQuantity;
        return;
      }
      const value = Number.parseInt(raw, 10);
      if (value === Number(item.dataset.cartItemQuantity)) {
        input.value = item.dataset.cartItemQuantity;
        return;
      }
      changeLine(item, value);
    });

    container.addEventListener('keydown', (event) => {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (!input || event.key !== 'Enter') return;
      // Enter applies the typed quantity instead of submitting the cart form.
      event.preventDefault();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  /* ------------------------------------------------------------------ */
  /* Drawer                                                              */
  /* ------------------------------------------------------------------ */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.panel = this.querySelector('.cart-drawer__panel');
      this.opener = null;
      this.inertedNodes = [];

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });

      this.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });

      const contents = this.querySelector('[data-cart-drawer-contents]');
      if (contents) bindLineControls(this);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) {
        this.focusTitle();
        return;
      }
      this.opener = trigger || document.activeElement;
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((node) => node.setAttribute('aria-expanded', 'true'));
      this.setBackgroundInert(true);
      window.requestAnimationFrame(() => this.focusTitle());
    }

    focusTitle() {
      const title = this.querySelector('#CartDrawerTitle');
      (title || this.panel).focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((node) => node.setAttribute('aria-expanded', 'false'));
      this.setBackgroundInert(false);
      const opener = this.opener;
      this.opener = null;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    }

    setBackgroundInert(isInert) {
      const host = this.closest('.shopify-section') || this;
      if (isInert) {
        this.inertedNodes = [...document.body.children].filter(
          (node) => node !== host && node.tagName !== 'SCRIPT' && !node.hasAttribute('inert')
        );
        this.inertedNodes.forEach((node) => node.setAttribute('inert', ''));
      } else {
        this.inertedNodes.forEach((node) => node.removeAttribute('inert'));
        this.inertedNodes = [];
      }
    }

    trapFocus(event) {
      const nodes = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (node) => !node.hidden && node.offsetParent !== null
      );
      if (!nodes.length) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !this.panel.contains(active) || active === this.querySelector('#CartDrawerTitle'))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  const getDrawer = () => document.querySelector('cart-drawer[data-cart-drawer]');

  const openDrawer = (trigger) => {
    const drawer = getDrawer();
    if (drawer && cartSettings.type === 'drawer') {
      drawer.open(trigger);
      return true;
    }
    return false;
  };

  /* ------------------------------------------------------------------ */
  /* Cart page                                                           */
  /* ------------------------------------------------------------------ */
  const bindCartPage = (page) => {
    if (page.dataset.bound) return;
    page.dataset.bound = 'true';
    bindLineControls(page);

    let noteTimer = null;
    page.addEventListener('input', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(() => {
        client.updateNote(note.value).catch((error) => {
          showContainerError(page.querySelector('[data-cart-page-contents]'), error.message || GENERIC_ERROR);
        });
      }, 400);
    });
  };

  /* Wait for queued cart requests before leaving for checkout, so the checkout gets the final cart. */
  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout' || !client.busy) return;
    const form = event.target;
    event.preventDefault();
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    await client.idle();
    // The cart update re-renders the drawer and cart page contents, which replaces this form;
    // submit the form that is on the page now so the click is never lost.
    let liveForm = form;
    if (!form.isConnected) {
      let selector = null;
      if (form.matches('[data-cart-checkout-form]')) selector = '[data-cart-checkout-form]';
      else if (form.matches('[data-cart-page-form]')) selector = '[data-cart-page-form]';
      liveForm = selector ? document.querySelector(selector) : null;
    }
    const liveButton = liveForm && liveForm.querySelector('[name="checkout"]');
    if (!liveForm || !liveButton || (liveButton !== submitter && liveButton.disabled)) {
      // The cart is now empty (no checkout control), so stay on the page.
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
      return;
    }
    liveButton.setAttribute('aria-busy', 'true');
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'checkout';
    hidden.value = submitter.value || liveButton.value || '';
    liveForm.appendChild(hidden);
    liveForm.submit();
  });

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                         */
  /* ------------------------------------------------------------------ */
  const add = async (formData, options = {}) => {
    if (!(formData instanceof FormData)) throw new CartRequestError(GENERIC_ERROR, 0, null);
    const id = formData.get('id');
    if (!id) throw new CartRequestError(GENERIC_ERROR, 0, null);
    try {
      const data = await client.add(formData);
      if (cartSettings.type === 'page') {
        window.location.href = routes.cart;
        return data;
      }
      if (!openDrawer(options.trigger)) window.location.href = routes.cart;
      return data;
    } catch (error) {
      dispatch('ehl:cart:error', { message: error.message });
      throw error;
    }
  };

  // Forms no other script handled (quick add, simple product forms).
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
    const isProductForm = form.matches('[data-product-form]') || /\/cart\/add\/?$/.test(form.getAttribute('action') || '');
    if (!isProductForm) return;
    const submitter = event.submitter;
    if (submitter && submitter.name && submitter.name !== 'add') return;
    if (cartSettings.type !== 'drawer' || !getDrawer()) return; // native submit goes to the cart page
    event.preventDefault();
    if (form.dataset.cartBusy === 'true') return;

    const button = submitter || form.querySelector('[name="add"], [type="submit"]');
    const label = button && button.querySelector('[data-add-to-cart-label]');
    const originalLabel = label ? label.textContent : '';
    const errorNode =
      form.querySelector('[data-add-to-cart-error]') ||
      (form.closest('[data-product-section]') && form.closest('[data-product-section]').querySelector('[data-add-to-cart-error]'));

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }
    form.dataset.cartBusy = 'true';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (label && strings.adding) label.textContent = strings.adding;

    try {
      await add(new FormData(form), { trigger: button });
    } catch (error) {
      const message = (error && error.message) || GENERIC_ERROR;
      if (errorNode) {
        errorNode.textContent = message;
        errorNode.hidden = false;
      } else {
        window.alert(message);
      }
    } finally {
      delete form.dataset.cartBusy;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      if (label) label.textContent = originalLabel;
    }
  });

  // Fallback path of the product page script: it added the item itself and asks for the result to be shown.
  document.addEventListener('ehl:cart:added', (event) => {
    const detail = event.detail || {};
    if (detail.handled || cartSettings.type !== 'drawer' || !getDrawer()) return;
    detail.handled = true;
    client
      .refresh()
      .catch(() => {})
      .finally(() => openDrawer(detail.trigger));
  });

  /* ------------------------------------------------------------------ */
  /* Header cart icon and page lifecycle                                 */
  /* ------------------------------------------------------------------ */
  // The cart can change in another tab. Compare the rendered drawer lines with /cart.js and
  // re-render (focus is kept) only when they differ.
  const renderedSignature = () => {
    const contents = document.querySelector('[data-cart-drawer] [data-cart-drawer-contents]');
    if (!contents) return null;
    return [...contents.querySelectorAll('[data-cart-item-key]')]
      .map((item) => `${item.dataset.cartItemKey}:${item.dataset.cartItemQuantity}`)
      .join('|');
  };

  const reconcileCart = async () => {
    if (client.busy || renderedSignature() === null) return;
    let cart = null;
    try {
      cart = await client.fetchCart();
    } catch (error) {
      return;
    }
    if (client.busy || !cart || !Array.isArray(cart.items)) return;
    const actual = cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
    if (actual !== renderedSignature()) client.refresh().catch(() => {});
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (!toggle || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (cartSettings.type !== 'drawer' || !getDrawer()) return;
    event.preventDefault();
    openDrawer(toggle);
    reconcileCart();
  });

  let pageWasHidden = false;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      pageWasHidden = true;
    } else if (pageWasHidden) {
      pageWasHidden = false;
      reconcileCart();
    }
  });

  const init = () => {
    document.querySelectorAll('[data-cart-toggle]').forEach((node) => {
      if (cartSettings.type === 'drawer' && getDrawer()) {
        node.setAttribute('aria-haspopup', 'dialog');
        node.setAttribute('aria-expanded', 'false');
        node.setAttribute('aria-controls', 'CartDrawer');
      }
    });
    document.querySelectorAll('[data-cart-page]').forEach(bindCartPage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Back/forward: the page may come from the bfcache (persisted) or from the HTTP cache
  // (navigation type back_forward); in both cases the cart may have changed since.
  window.addEventListener('pageshow', (event) => {
    let backForward = false;
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      backForward = Boolean(entry && entry.type === 'back_forward');
    } catch (error) {
      backForward = false;
    }
    if (event.persisted || backForward) client.refresh().catch(() => {});
  });

  // Theme editor: re-bind after the cart section reloads.
  document.addEventListener('shopify:section:load', () => init());

  EHL.cart = {
    __ehl: true,
    add,
    change: (target, quantity) => changeLine(target, quantity),
    refresh: () => client.refresh(),
    open: (trigger) => openDrawer(trigger),
    close: () => {
      const drawer = getDrawer();
      if (drawer) drawer.close();
    },
    idle: () => client.idle(),
  };
})();
